import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { api } from '../../services/api';
import { 
  FileText, 
  Search, 
  RefreshCw, 
  Download,
  MapPin,
  Truck,
  DollarSign,
  Calendar,
  MessageSquare,
  Loader,
  User
} from 'lucide-react';

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  background: white;
  color: ${({ theme }) => theme.colors.textPrimary};
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  
  &:hover {
    background: ${({ theme }) => theme.colors.gray50};
  }
`;

const SearchBox = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const SearchInput = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  
  input {
    flex: 1;
    padding: ${({ theme }) => theme.spacing.md};
    border: 1px solid ${({ theme }) => theme.colors.gray200};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    
    &:focus {
      outline: none;
      border-color: ${({ theme }) => theme.colors.primary};
    }
  }
`;

const TableContainer = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  text-align: left;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textSecondary};
  background: ${({ theme }) => theme.colors.gray50};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray200};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const Td = styled.td`
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray100};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const Tr = styled.tr`
  &:hover {
    background: ${({ theme }) => theme.colors.gray50};
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px;
  color: ${({ theme }) => theme.colors.textSecondary};
  
  svg {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${({ theme }) => theme.colors.textSecondary};
  
  svg {
    margin-bottom: 16px;
    opacity: 0.5;
  }
  
  h3 {
    margin: 0 0 8px 0;
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const InfoCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const ValorCell = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.success};
  font-size: 14px;
`;

const ComentarioCell = styled.div`
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-style: italic;
`;

const ProveedorBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: ${({ theme }) => theme.colors.primary}15;
  color: ${({ theme }) => theme.colors.primary};
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
`;

interface Licitacion {
  id: number;
  proveedor: string;
  origen_ciudad: string;
  destino_ciudad: string;
  tipo_vehiculo: string;
  tipo_carga: string;
  tipo_carroceria: string;
  ciudad_devolucion: string | null;
  valor_propuesto: number;
  comentarios: string;
  porcentaje_intermediacion: number | null;
  costos_adicionales: number | null;
  valor_adicional: number | null;
  fecha_propuesta: string;
  fecha_actualizacion: string;
}

const LicitacionesModule: React.FC = () => {
  const [licitaciones, setLicitaciones] = useState<Licitacion[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const cargarLicitaciones = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/licitaciones');
      setLicitaciones(response.data.licitaciones || []);
    } catch (error) {
      console.error('Error cargando licitaciones:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarLicitaciones();
  }, []);

  const licitacionesFiltradas = licitaciones.filter(l => 
    l.proveedor?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    l.origen_ciudad?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    l.destino_ciudad?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    l.tipo_vehiculo?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const descargarExcel = () => {
    const headers = ['Proveedor', 'Origen', 'Destino', 'Tipo Vehículo', 'Tipo Carga', 'Tipo Carrocería', 'Ciudad Devolución', 'Valor Propuesto', 'Comentarios', '% Intermediación', 'Costos Adicionales', 'Valor Adicional', 'Fecha Propuesta'];
    const rows = licitacionesFiltradas.map(l => [
      l.proveedor || '',
      l.origen_ciudad || '',
      l.destino_ciudad || '',
      l.tipo_vehiculo || '',
      l.tipo_carga || '',
      l.tipo_carroceria || '',
      l.ciudad_devolucion || '',
      l.valor_propuesto || 0,
      l.comentarios || '',
      l.porcentaje_intermediacion ?? '',
      l.costos_adicionales ?? '',
      l.valor_adicional ?? '',
      l.fecha_propuesta ? new Date(l.fecha_propuesta).toLocaleDateString() : ''
    ]);

    const csvContent = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `licitaciones_comex_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (value: number) => {
    if (!value) return '-';
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(value);
  };

  return (
    <Container>
      <Header>
        <Title>
          <FileText size={28} />
          Licitaciones / Propuestas
        </Title>
        <Actions>
          <Button onClick={descargarExcel}>
            <Download size={18} />
            Descargar Excel
          </Button>
          <Button onClick={cargarLicitaciones}>
            <RefreshCw size={18} />
            Actualizar
          </Button>
        </Actions>
      </Header>

      <SearchBox>
        <SearchInput>
          <Search size={20} color="#64748b" />
          <input
            type="text"
            placeholder="Buscar por proveedor, origen, destino o tipo de vehículo..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchInput>
      </SearchBox>

      <TableContainer>
        {loading ? (
          <LoadingContainer>
            <Loader size={32} />
            <span style={{ marginLeft: '12px' }}>Cargando licitaciones...</span>
          </LoadingContainer>
        ) : licitacionesFiltradas.length === 0 ? (
          <EmptyState>
            <FileText size={48} />
            <h3>No hay propuestas registradas</h3>
            <p>Las propuestas de los proveedores aparecerán aquí.</p>
          </EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Proveedor</Th>
                <Th>Ruta</Th>
                <Th>Vehículo</Th>
                <Th>Valor Propuesto</Th>
                <Th>% Interm.</Th>
                <Th>Costos Adic.</Th>
                <Th>Ciudad Devolución</Th>
                <Th>Valor Adicional</Th>
                <Th>Comentarios</Th>
                <Th>Fecha</Th>
              </tr>
            </thead>
            <tbody>
              {licitacionesFiltradas.map((licitacion) => (
                <Tr key={licitacion.id}>
                  <Td>
                    <ProveedorBadge>
                      <User size={14} />
                      {licitacion.proveedor}
                    </ProveedorBadge>
                  </Td>
                  <Td>
                    <InfoCell>
                      <InfoRow>
                        <MapPin />
                        <strong>{licitacion.origen_ciudad}</strong> → <strong>{licitacion.destino_ciudad}</strong>
                      </InfoRow>
                      <InfoRow>
                        Carga: {licitacion.tipo_carga} | Carrocería: {licitacion.tipo_carroceria}
                      </InfoRow>
                    </InfoCell>
                  </Td>
                  <Td>
                    <InfoRow>
                      <Truck />
                      {licitacion.tipo_vehiculo}
                    </InfoRow>
                  </Td>
                  <Td>
                    <ValorCell>
                      {formatCurrency(licitacion.valor_propuesto)}
                    </ValorCell>
                  </Td>
                  <Td>
                    {licitacion.porcentaje_intermediacion != null ? `${licitacion.porcentaje_intermediacion}%` : '-'}
                  </Td>
                  <Td>
                    {licitacion.costos_adicionales != null ? formatCurrency(licitacion.costos_adicionales) : '-'}
                  </Td>
                  <Td>
                    {licitacion.ciudad_devolucion || '-'}
                  </Td>
                  <Td>
                    {licitacion.valor_adicional != null ? formatCurrency(licitacion.valor_adicional) : '-'}
                  </Td>
                  <Td>
                    <ComentarioCell title={licitacion.comentarios || ''}>
                      {licitacion.comentarios ? (
                        <>
                          <MessageSquare size={12} style={{ display: 'inline', marginRight: '4px' }} />
                          {licitacion.comentarios}
                        </>
                      ) : '-'}
                    </ComentarioCell>
                  </Td>
                  <Td>
                    <InfoRow>
                      <Calendar />
                      {formatDate(licitacion.fecha_propuesta)}
                    </InfoRow>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </TableContainer>
    </Container>
  );
};

export default LicitacionesModule;

